import dataService from './DataService';
import TimeStamp from '../utils/TimeStamp';

/**
 * Service pour la gestion des factures
 * Fournit des méthodes pour créer et manipuler les factures
 */
class BillService {
  /**
   * Initialise le service de gestion des factures
   * Le préfixe utilisé pour les identifiants de facture
   */
  constructor() {
    this.PREFIX = 'F';
  }

  /**
   * Calcule l'identifiant personnalisé pour une nouvelle facture
   * @param {Object} quote - Le devis associé à la facture
   * @param {string} quote.quoteCustomId - L'identifiant personnalisé du devis
   * @param {string} quote.id - L'identifiant unique du devis
   * @returns {Promise<string>} L'identifiant personnalisé de la facture
   */
  async calculateBillCustomId(quote) {
    const quoteCustomId = quote.quoteCustomId;
    const prefix = this.PREFIX;
    const existingBills = await this.getExistingBills(quote.id);
    const currentBillNumber = existingBills.length + 1;
    const suffix = `-${currentBillNumber}`;
    return `${prefix}${quoteCustomId}${suffix}`;
  }

  /**
   * Récupère toutes les factures existantes pour un devis donné
   * @param {string} quoteId - L'identifiant du devis
   * @returns {Promise<Array>} La liste des factures associées au devis
   */
  async getExistingBills(quoteId) {
    const existingBills = await dataService.db.bills
    .where('quoteId')
    .equals(quoteId)
    .reverse()
    .sortBy('billCustomId');

    return existingBills;
  }

  /**
   * Récupère la dernière facture pour un devis donné en utilisant le drapeau isLastBill
   * @param {string} quoteId - L'identifiant du devis
   * @returns {Promise<Object|null>} La dernière facture ou null si aucune n'existe
   */
  async getLastBill(quoteId) {
    const lastBill = await dataService.db.bills
    .where('quoteId')
    .equals(quoteId)
    .and(bill => bill.isLastBill)
    .first();

    return lastBill;
  }

  /**
   * Récupère la dernière facture non annulée pour un devis donné
   * @param {string} quoteId - L'identifiant du devis
   * @returns {Promise<Object|null>} La dernière facture non annulée ou null si aucune n'existe
   */
  async getLastNotCanceledBill(quoteId) {
    const bills = await dataService.db.bills
      .where('quoteId')
      .equals(quoteId)
      .and(bill => bill.status !== 'Canceled')
      .reverse()
      .sortBy('timeStamp');

    return bills.length > 0 ? bills[0] : null;
  }

  /**
   * Crée une nouvelle facture à partir d'un devis
   * @param {Object} quote - Le devis à partir duquel créer la facture
   * @param {string} quote.id - L'identifiant unique du devis
   * @param {string} quote.quoteCustomId - L'identifiant personnalisé du devis
   * @param {string} quote.opportunityId - L'identifiant de l'opportunité liée au devis
   * @returns {Promise<Object>} La facture créée
   * @throws {Error} Si les lignes du devis ne sont pas trouvées ou en cas d'erreur
   */
  async createBillFromQuote(quote) {

    const optionProductId = await dataService.getOptionProductId();

    try {
      // Get quote lines hierarchy
      const quoteWithLines = await dataService.getQuoteWithLines(quote.id);
      if (!quoteWithLines || !quoteWithLines.lines) {
        throw new Error('Quote lines not found');
      }

      // Generate billCustomId
      const billCustomId = await this.calculateBillCustomId(quote);

      const lastBill = await this.getLastBill(quote.id);

      const lastNotCanceledBill = await this.getLastNotCanceledBill(quote.id);

      const quoteTotalAmount = await quote.getTotal();

      // Obtenir les informations de l'entreprise depuis le compte de l'entreprise
      const companyAccount = await dataService.getCompanyAccount();

      // Récupérer le taux de TVA depuis les paramètres
      const vatRateSetting = await dataService.db.settings.where('name').equals('TVA20').first();
      const vatRate = vatRateSetting; // Taux par défaut: 20%

      // Obtenir les informations du client
      const opportunityWithQuotes = await dataService.getOpportunityWithQuotes(quote.opportunityId);
      const account = opportunityWithQuotes ? await dataService.getAccountWithDetails(opportunityWithQuotes.accountId) : null;
      
      // Obtenir les contacts et adresses de facturation
      // const billingContact = account ? 
      //   await dataService.db.contacts.where('accountId').equals(account.id)
      //   .and(contact => contact.isBillingContact).first() : null;
      
      const billingLocation = account ? 
        account.addresses.find(location => location.isBillingLocation) || 
        account.addresses.find(location => location.isPrimary) : null;

      // Calculer les montants
      const netAmount = quoteTotalAmount / (1 + (vatRate / 100));
      const vatAmount = quoteTotalAmount - netAmount;

      // Date d'échéance par défaut: +30 jours
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 30);
      const paymentDueDate = TimeStamp.fromDate(dueDate);

      // Create new bill
      const bill = await dataService.add('bills', {
        billCustomId,
        quoteId: quote.id,
        status: 'Draft',
        timeStamp: TimeStamp.fromDate(new Date()),
        command: lastBill ? lastBill.command : null,
        totalAmount: quoteTotalAmount,
        alreadyPaid: lastNotCanceledBill ? lastNotCanceledBill.currentProgressAmount : 0,
        currentProgressAmount: lastNotCanceledBill ? lastNotCanceledBill.currentProgressAmount : 0,
        isLastBill: true,
        // Champs EN16931
        sellerTaxID: companyAccount?.taxId || '',
        sellerAddress: companyAccount?.addresses?.find(a => a.isPrimary || a.isBillingLocation) ? 
          `${companyAccount.addresses.find(a => a.isPrimary || a.isBillingLocation).address}, ${companyAccount.addresses.find(a => a.isPrimary || a.isBillingLocation).postalCode} ${companyAccount.addresses.find(a => a.isPrimary || a.isBillingLocation).city}` : '',
        customerTaxID: account?.taxId || '',
        customerBillingAddress: billingLocation ? 
          `${billingLocation.address}, ${billingLocation.postalCode} ${billingLocation.city}` : '',
        contractReference: quote.quoteCustomId,
        issueDate: TimeStamp.fromDate(new Date()),
        paymentMethod: 'CREDIT_TRANSFER', // Virement bancaire par défaut
        paymentReference: billCustomId,
        paymentTerms: '30 jours fin de mois',
        vatAmount: vatAmount,
        vatRate: vatRate,
        currencyCode: 'EUR',
        invoiceType: 'COMMERCIAL_INVOICE',
        paymentDueDate: paymentDueDate,
        netAmount: netAmount
      });
      
      // Update last bill to not be the last one
      if (lastBill) {
        await dataService.update('bills', lastBill.id, {
          ...lastBill,
          isLastBill: false
        });
      }

      // Create bill lines recursively
      const createBillLines = async (quoteLines, parentLineId = null) => {

        for (const quoteLine of quoteLines) {
          if(parseInt(quoteLine.productId) === optionProductId) {
            continue;
          }
          
          // Get the product if available
          const product = quoteLine.productId ? 
            await dataService.getById('products', quoteLine.productId) : null;
          
          const linePrice = !parentLineId ? await quoteLine.getFinalPrice() : 0;
          const lineNetAmount = linePrice / (1 + (vatRate / 100));
          const lineVatAmount = linePrice - lineNetAmount;
          
          // Create bill line
          const billLine = {
            description: quoteLine.description,
            billId: bill.id,
            parentLineId: parentLineId,
            productId: quoteLine.productId,
            progress: 0,
            lastProgess: lastBill ? 0 : 0, // Will be updated from last bill if exists
            // Only set price for main lines (no parentLineId) using getFinalPrice
            price: linePrice,
            // Champs EN16931
            itemIdentifier: product?.id || '',
            quantity: quoteLine.quantity || 1,
            unitOfMeasure: 'EA', // each/unité
            unitPrice: quoteLine.unitPrice || 0,
            lineVatRate: vatRate,
            lineVatAmount: lineVatAmount,
            lineNetAmount: lineNetAmount,
            lineTotalAmount: linePrice,
            vatCategory: 'S', // S=standard
            invoicePeriodStart: bill.timeStamp,
            invoicePeriodEnd: bill.timeStamp
          };

          const createdBillLine = await dataService.add('billLines', billLine);

          // Recursively create sub-lines
          if (quoteLine.subLines && quoteLine.subLines.length > 0) {
            await createBillLines(quoteLine.subLines, createdBillLine.id);
          }
        }
      };

      // Start creating bill lines
      await createBillLines(quoteWithLines.lines);

      // If there was a last bill, update lastProgress from its lines
      if (lastBill) {
        const lastBillLines = await dataService.getBillLineHierarchy(lastBill.id);
        const updateLastProgress = async (currentLines, lastLines) => {
          for (const currentLine of currentLines) {
            const matchingLastLine = lastLines.find(ll => ll.description === currentLine.description);
            if (matchingLastLine) {
              await dataService.update('billLines', currentLine.id, {
                ...currentLine,
                lastProgess: matchingLastLine.progress,
                progress: matchingLastLine.progress
              });
            }
            
            if (currentLine.subLines && currentLine.subLines.length > 0 && matchingLastLine && matchingLastLine.subLines) {
              await updateLastProgress(currentLine.subLines, matchingLastLine.subLines);
            }
          }
        };

        const currentBillLines = await dataService.getBillLineHierarchy(bill.id);
        await updateLastProgress(currentBillLines, lastBillLines);
      }

      return bill;
    } catch (error) {
      console.error('Error creating bill from quote:', error);
      throw error;
    }
  }
}

const billService = new BillService();
export default billService;
